/**
 * Make scroll-to-top work consistently in every layout, with smooth
 * animation, including the iOS Safari iframe case.
 *
 * The companion CSS rule in `index.css` (`#root { position: fixed;
 * inset: 0; overflow-y: auto; -webkit-overflow-scrolling: touch }`)
 * forces iOS Safari to respect the iframe's height (works around the
 * iframe content-expansion bug WebKit #172854). The actual page scroll
 * therefore lives on `#root` — not on `window`, not on `<main>`. This
 * patch routes every `window.scrollTo(...)` call to `#root`.
 *
 * iOS Safari 15+ has known bugs where `scrollTo({ behavior: 'smooth' })`
 * silently fails on internal scroll containers (MDN browser-compat-data
 * #11037, smoothscroll-polyfill #177). We bypass the native smooth and
 * animate ourselves with `requestAnimationFrame` + an easeInOutCubic
 * curve so the animation looks identical everywhere.
 *
 * The patch also promotes legacy `window.scrollTo(0, 0)` calls (~40 call
 * sites across screens) to smooth, since the bulk of the code pre-dates
 * the smooth migration and would otherwise teleport.
 */

const ANIMATION_DURATION_MS = 400;

// easeInOutCubic — symmetrical ease-in/out, standard for scroll animations.
function ease(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function animateRootScrollTo(top: number): void {
  const root = document.getElementById('root');
  if (!root) return;

  const start = root.scrollTop;
  const distance = top - start;
  if (distance === 0) return;

  const startTime = performance.now();

  function step(now: number): void {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
    const eased = ease(progress);
    root!.scrollTop = start + distance * eased;
    if (progress < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

(window as unknown as { scrollTo: typeof window.scrollTo }).scrollTo = function patchedScrollTo(
  xOrOptions: number | ScrollToOptions,
  y?: number,
): void {
  // Normalise both call signatures into a single options object.
  const options: ScrollToOptions =
    typeof xOrOptions === 'object'
      ? { ...xOrOptions }
      : { left: xOrOptions, top: y ?? 0 };

  const targetTop = options.top ?? 0;
  // Default to smooth for scroll-to-top calls that don't specify a behaviour.
  // Most legacy `window.scrollTo(0, 0)` call sites benefit from this.
  if (targetTop === 0 && options.behavior === undefined) {
    options.behavior = 'smooth';
  }

  if (options.behavior === 'smooth') {
    animateRootScrollTo(targetTop);
  } else {
    // Instant / 'auto' — set scrollTop directly.
    const root = document.getElementById('root');
    if (root) root.scrollTop = targetTop;
  }
} as typeof window.scrollTo;
